"use client";

import Link from "next/link";
import type { CSSProperties, ReactNode } from "react";
import { ArrowRight, ArrowUpRight } from "@/components/icons";
import { cn } from "@/lib/utils";
import type { ToolAccent, ToolIcon } from "./types";

/* Accent pair for the /tools pages. `fill` is the tint used for chip and pill
   backgrounds and borders — it stays identical in both themes because it is
   only ever laid down at low alpha. `text` is the readable step, which darkens
   on the light theme (orange especially: #ff8a5b would sit near 2:1 on white,
   where --yellow-accent flips to #c2410c and clears AA). */
const ACCENT: Record<ToolAccent, { fill: string; text: string }> = {
  blue: { fill: "var(--brand-green)", text: "var(--brand-green)" },
  orange: { fill: "var(--brand-yellow)", text: "var(--yellow-accent)" },
};

const tint = (accent: ToolAccent, pct: number) =>
  `color-mix(in srgb, ${ACCENT[accent].fill} ${pct}%, transparent)`;

/** The shared card shell for every tile on the tools pages.
 *
 *  `card-wash-blue` is what keeps a grid off flat white on the light theme — it
 *  bleeds blue in from one of the card's corners, rotating which corner every
 *  third sibling. The blue-only variant, not the blue+orange `.card-wash`: these
 *  grids run 6–8 tiles, and at that count the warm corner reads as a second kind
 *  of card rather than a hint. The rotation is anchored on `.card-wash-blue`
 *  siblings, or on a `.feature-tile` wrapper when the cards sit inside per-card
 *  motion wrappers (see globals.css) — so give those wrappers `feature-tile`, or
 *  the whole grid gets the same tint. */
export const toolCardClass =
  "bg-card card-wash-blue card-hover border-border relative flex h-full flex-col overflow-hidden rounded-2xl border p-6";

interface IconChipProps {
  icon: ToolIcon;
  accent?: ToolAccent;
  /** md = 40px chip (card headers), sm = 36px (dense panels and list items). */
  size?: "sm" | "md";
  className?: string;
}

/** Tinted, bordered square holding a single accent-coloured icon. */
export function AccentIconChip({
  icon: Icon,
  accent = "blue",
  size = "md",
  className,
}: IconChipProps) {
  return (
    <span
      className={cn(
        "relative flex shrink-0 items-center justify-center rounded-lg border",
        size === "md" ? "size-10" : "size-9",
        className
      )}
      style={{ backgroundColor: tint(accent, 12), borderColor: tint(accent, 24) }}
    >
      <Icon
        size={size === "md" ? 20 : 18}
        style={{ color: ACCENT[accent].text }}
        aria-hidden="true"
      />
    </span>
  );
}

interface AccentPillProps {
  label: string;
  accent?: ToolAccent;
  icon?: ToolIcon;
  className?: string;
}

/** Status pill — "Available", "Coming soon", "Required", "Optional". */
export function AccentPill({ label, accent = "blue", icon: Icon, className }: AccentPillProps) {
  return (
    <span
      className={cn(
        "relative flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold",
        className
      )}
      style={{
        color: ACCENT[accent].text,
        borderColor: tint(accent, 24),
        backgroundColor: tint(accent, 10),
      }}
    >
      {Icon && <Icon size={11} aria-hidden="true" />}
      {label}
    </span>
  );
}

/** Accent-coloured text, for the readable step of either hue. */
export function accentTextStyle(accent: ToolAccent = "blue"): CSSProperties {
  return { color: ACCENT[accent].text };
}

interface ToolCardProps {
  icon: ToolIcon;
  title: string;
  description: string;
  accent?: ToolAccent;
  /** The hub's extension grid is the page's card-level h2 set; everything else is h3. */
  titleAs?: "h2" | "h3";
  badge?: { label: string; icon?: ToolIcon; accent?: ToolAccent };
  /** Turns the whole card into a link. */
  href?: string;
  /** Pass `false` when the destination owns its own scroll reset — the tool
      segment starts with the fixed header's spacer, so Next's scroll-the-
      changed-segment-into-view lands ~30px down the page and beats that reset. */
  scroll?: boolean;
  /** Footer action. `solid` is the filled pill, `link` the accent text + arrow. */
  cta?: { label: string; style: "solid" | "link" };
  /** Extra footer content (use-case links, code chips…), below the description. */
  children?: ReactNode;
  className?: string;
}

/** Icon-chip card used by every grid on the tools hub and the tool landings. */
export function ToolCard({
  icon,
  title,
  description,
  accent = "blue",
  titleAs: Title = "h3",
  badge,
  href,
  scroll,
  cta,
  children,
  className,
}: ToolCardProps) {
  const body = (
    <>
      <div className="relative flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <AccentIconChip icon={icon} accent={accent} />
          <Title className="text-foreground text-base font-semibold tracking-[-0.01em] sm:text-lg">
            {title}
          </Title>
        </div>
        {badge && (
          <AccentPill label={badge.label} icon={badge.icon} accent={badge.accent ?? accent} />
        )}
      </div>

      <p className="text-muted-foreground relative mt-3 text-sm leading-relaxed">{description}</p>

      {children}

      {cta && (
        <div className="relative mt-auto pt-5">
          {cta.style === "solid" ? (
            <span className="bg-primary text-primary-foreground inline-flex w-fit items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-opacity group-hover:opacity-90">
              {cta.label}
              <ArrowUpRight size={14} aria-hidden="true" />
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 text-sm font-semibold"
              style={accentTextStyle(accent)}
            >
              {cta.label}
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </span>
          )}
        </div>
      )}
    </>
  );

  if (!href) return <div className={cn(toolCardClass, "group", className)}>{body}</div>;

  return (
    <Link
      href={href}
      scroll={scroll}
      className={cn(toolCardClass, "card-spotlight group", className)}
    >
      {body}
    </Link>
  );
}
